// #############################################################################
// #
// #  German translations
// #
// #############################################################################
// This file contains the German translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for german-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to german-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langDe = {
  home: {
    welcome: 'Willkommen zum {service} Service',
    enter: 'Eingabe'
  },
  account_type: {
    publisher: 'Verleger',
    consumer: 'Aggregator',
    author: 'Autor'
  },
  app: {
    accountNotSubscribedToCurrentService: 'Ihr Konto ist nicht für den aktuellen Service abonniert'
  },
  login: {
    continue: 'Weiter',
    login_tab: 'Anmeldung',
    logout: 'Abmelden',
    register_tab: 'Registrierung',
    loginTitle: 'Bei Ihrem Konto anmelden',
    chooseSignInMethod: 'Wählen Sie, wie Sie sich anmelden möchten',
    noAccount: 'Haben Sie noch kein Konto?',
    registerButton: 'Konto erstellen',
    cancel: 'Abbrechen',
    identifier: 'E-Mail',
    identifierDescription: 'Sie können sich mit Ihrer E-Mail anmelden',
    identifierPlaceholder: 'Geben Sie Ihre E-Mail ein',
    usernameOrEmail: 'Benutzername oder E-Mail',
    useAnotherAccount: 'Anderes Konto verwenden',
    username: 'Benutzername',
    password: 'Passwort',
    email: 'E-Mail',
    loginButton: 'Anmelden',
    signingIn: 'Anmelden...',
    signInWithEmail: 'Mit E-Mail anmelden',
    signInWithEmailAgain: 'Neuen Anmelde-Link per E-Mail anfordern',
    signInWithEmailDescription: 'Verwenden Sie Ihre Kontoe-Mail',
    signingInWithMagicLink: 'Anmelden mit Magic-Link...',
    signInWithEmailMessageSent: 'Eine Magic-Link wurde an Ihre E-Mail gesendet, folge dem Link um sich anzumelden.',
    signingInWithEmail: 'Anmelden mit E-Mail...',
    subscribeButton: 'Abonnieren Sie {type}',
    registerAsButton: 'Registrieren Sie sich als {type}',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort ausblenden',
    or: 'oder',
    subscriptionSuccess: 'Abonnement erfolgreich',
    alternativeAccountDescription: 'Ihr Konto ist derzeit nicht bei dem aktuellen Dienst abonniert. Sie können abonnieren oder zu den folgenden Diensten wechseln.',
    alternativeAccountGoTo: 'Gehen Sie zu {type}',
    sameAccount: 'Sie sind bereits mit demselben Konto angemeldet.',
    sessionConflict: {
      title: 'Sitzungskonflikt',
      message: 'Eine Sitzung ist bereits für ein anderes Konto geöffnet.',
      currentSession: 'Aktuelle Sitzung:',
      magicLinkSession: 'Magic-Link-Sitzung:',
      question: 'Möchten Sie mit der aktuellen Sitzung verbunden bleiben oder mit der Magic-Link-Sitzung neu verbinden?',
      stayWithCurrent: 'Mit aktueller Sitzung verbinden',
      reconnectWithMagicLink: 'Mit Magic-Link-Sitzung neu verbinden'
    },
    error: {
      invalidCredentials: 'Ungültige Anmeldeinformationen',
      loginFailed: 'Anmeldung fehlgeschlagen',
      apiConnection: 'API-Verbindungsfehler',
      subscribeFailed: 'Abonnement fehlgeschlagen: {error}',
      signInWithEmailFailed: 'Anmeldung per E-Mail fehlgeschlagen: {error} (überprüfen Sie Ihre E-Mail oder Ihren Benutzernamen).',
      invalidEmailFormat: 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    },
    alternativeAccount: {
      contentCreators: 'Sie sind ein Inhaltsersteller?',
      contentCreatorsLink: 'Melden Sie sich hier an',
      authors: 'Sie sind ein Autor?',
      authorsLink: 'Melden Sie sich hier an',
      aggregators: 'Sie sind ein Aggregator?',
      aggregatorsLink: 'Melden Sie sich hier an',
      author: 'Autor',
      consumer: 'Aggregator',
      publisher: 'Verleger'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: 'Kontoregistrierung',
    introduction: {
      title: 'Willkommen zur Registrierung',
      subtitle: 'Registrieren Sie sich für den {service} Service',
      description: 'Sie sind dabei, sich für den {service} Service zu registrieren. Diese Registrierung ermöglicht es Ihnen, auf alle Funktionen und Fähigkeiten zuzugreifen, die für {service} Konten verfügbar sind.',
      startRegistration: 'Registrierung starten',
      alreadyHaveAccount: 'Haben Sie bereits ein Konto? Anmelden'
    },
    step_introduction: {
      title: 'Einführung'
    },
    step_account_setup: {
      title: 'Profil-Einrichtung',
      displayName: 'Anzeigename',
      displayNamePlaceholder: 'Geben Sie Ihren Anzeigenamen ein',
      username: 'Benutzername',
      usernamePlaceholder: 'Geben Sie Ihren Benutzernamen ein',
      confirmPassword: 'Passwort bestätigen',
      passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
      password: 'Passwort',
      email: 'E-Mail',
      emailPlaceholder: 'Geben Sie Ihre E-Mail-Adresse ein',
      affiliation: 'Zugehörigkeit',
      affiliationPlaceholder: 'Universität, Unternehmen oder Institution',
      homePage: 'Homepage / Hauptseite',
      homePagePlaceholder: 'https://ihre-website.com',
      submit: 'Profil einreichen',
      submitting: 'Wird übermittelt...'
    },
    step_account_verification: {
      title: 'Kontoverifizierung',
      tokenLabel: 'Verifizierungstoken',
      tokenPlaceholder: 'Token eingeben',
      checkEmail: 'Wir haben eine Verifizierungs-E-Mail an Ihre Adresse gesendet. Bitte überprüfen Sie Ihren Posteingang und folgen Sie den Anweisungen zur Kontoverifizierung.',
      tokenSent: 'Verifizierungstoken gesendet an: {email}',
      resendToken: 'Token erneut senden',
      verifyAccount: 'Konto verifizieren',
      sendingEmail: 'E-Mail wird gesendet...',
      resendEmail: 'E-Mail erneut senden',
      accountVerified: 'Konto erfolgreich verifiziert!',
      redirecting: 'Weiterleitung zum nächsten Schritt...',
      verifying: 'Aktivierung prüfen',
      sendingToken: 'Verifizierungstoken wird gesendet...',
      invalidToken: 'Ungültiger Verifizierungstoken. Bitte versuchen Sie es erneut.',
      tooManyAttempts: 'Zu viele fehlgeschlagene Versuche. Bitte fordern Sie einen neuen Token an.',
      noToken: 'Kein Verifizierungstoken gefunden. Bitte fordern Sie einen neuen Token an.',
      verificationSuccess: 'E-Mail-Verifizierung erfolgreich!',
      attemptsRemaining: '{count} Versuche verbleibend',
      resendSuccess: 'Neuer Verifizierungstoken an Ihre E-Mail gesendet.',
      networkError: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      checkingActivation: 'Kontoaktivierung wird überprüft...',
      emailSentTo: 'E-Mail gesendet an:',
      activationEmailResent: 'Aktivierungs-E-Mail erfolgreich gesendet! Bitte überprüfen Sie Ihren Posteingang.',
      resendFailed: 'Fehler beim Senden der Aktivierungs-E-Mail. Bitte versuchen Sie es erneut.',
      missingIdentifierOrRegistrationToken: 'Benutzerkennung oder Registrierungstoken fehlt',
      accountDoesNotHaveAuthorPrivileges: 'Konto hat keine Autorenrechte. Bitte kontaktieren Sie den Support.',
      accountActivationCheckFailed: 'Kontoaktivierungsprüfung fehlgeschlagen. Bitte versuchen Sie es erneut.',
      accountNotYetActivated: 'Konto noch nicht aktiviert. Bitte überprüfen Sie Ihre E-Mail und klicken Sie auf den Aktivierungslink.',
      invalidCredentials: 'Ungültige Anmeldeinformationen. Bitte überprüfen Sie Ihren Benutzernamen und Ihr Passwort.',
      failedToVerifyAccountActivation: 'Kontoaktivierungsverifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
      failedToResendActivationEmail: 'Fehler beim erneuten Senden der Aktivierungs-E-Mail. Bitte versuchen Sie es erneut.',
      accountRegistrationRejected: 'Kontoregistrierung abgelehnt. Bitte kontaktieren Sie den Support.'
    },
    step_registration_complete: {
      title: 'Registrierung abschließen',
      registrationSummary: 'Registrierungszusammenfassung',
      profileCreated: 'Profil erstellt',
      emailsSent: 'E-Mails an Verleger gesendet',
      continueToDashboard: 'Weiter zum Dashboard',
      continueToLogin: 'Weiter zur Anmeldung'
    }
  },
  common: {
    cancel: 'Abbrechen',
    save: 'Speichern'
  },
  navbar: {
    docs: 'Doks',
    account: 'Konto',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    login: 'Anmelden',
    pendingRegistrationDesc: 'Bitte füllen Sie Ihre Registrierung aus, um auf diesen Service zuzugreifen.',
    apps: {
      title: 'Apps',
      publisher: 'Verleger',
      publisherDesc: 'Wechseln Sie zur Publisher-Service',
      author: 'Autor',
      authorDesc: 'Wechseln Sie zur Author-Service',
      consumer: 'Aggregator',
      consumerDesc: 'Wechseln Sie zur Aggregator-Service',
      website: 'Website',
      websiteDesc: 'Wechseln Sie zur Website',
      portal: 'Portal',
      portalDesc: 'Wechseln Sie zum Portal',
      currentApplicationDesc: 'Aktueller Service',
      unavailableServiceDesc: 'Service nicht verfügbar'
    }
  },
  sidebar: {
    expand: 'Erweitern',
    collapse: 'Einklappen',
    info: 'Informationen',
    infoDesc: 'Informationen über den Service',
    showDescriptions: 'Beschreibungen anzeigen',
    hideDescriptions: 'Beschreibungen ausblenden'
  },
  pagination: {
    previous: 'Zurück',
    next: 'Weiter',
    first: 'Erste',
    last: 'Letzte',
    page: 'Seite {current} von {total}',
    itemsPerPage: 'Elemente pro Seite'
  },
  table: {
    loading: 'Lädt...',
    noData: 'Keine Daten',
    noResults: 'Keine Ergebnisse',
    search: 'Suche'
  },
  form: {
    submit: 'Absenden',
    cancel: 'Abbrechen',
    save: 'Speichern',
    loading: 'Lädt...'
  },
  modal: {
    close: 'Schließen',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
    save: 'Speichern'
  },
  alert: {
    success: 'Erfolg',
    error: 'Fehler',
    warning: 'Warnung',
    info: 'Informationen'
  },
  toast: {
    success: 'Erfolg',
    error: 'Fehler',
    warning: 'Warnung',
    info: 'Informationen',
    close: 'Schließen'
  },
  tooltip: {
    success: 'Erfolg',
    error: 'Fehler',
    warning: 'Warnung',
    info: 'Informationen'
  },
  notification: {
    success: 'Erfolg',
    error: 'Fehler',
    warning: 'Warnung',
    info: 'Informationen'
  },
  notifications: {
    title: 'Benachrichtigungen',
    empty: 'Keine Benachrichtigungen',
    loading: 'Benachrichtigungen werden geladen...',
    pageInfo: 'Seite {current} von {total}',
    error: 'Fehler beim Laden der Benachrichtigungen',
    markAsRead: 'Als gelesen markieren',
    markAsUnread: 'Als ungelesen markieren',
    open: 'Öffnen',
    markAllAsRead: 'Alle als gelesen markieren',
    unread: 'Ungelesen',
    all: 'Alle',
    noContent: 'Kein Inhalt verfügbar',
    close: 'Schließen',
    reload: 'Benachrichtigungen neu laden',
    showMore: 'Mehr anzeigen',
    loadingMore: 'Weitere Benachrichtigungen werden geladen...',
    errorLoadingMore: 'Fehler beim Laden weiterer Benachrichtigungen',
    noMoreNotifications: 'Keine weiteren Benachrichtigungen',
    noNotifications: 'Keine Benachrichtigungen',
    noNotificationsFound: 'Keine Benachrichtigungen gefunden'
  },
  account: {
    title: 'Kontoinformationen',
    profile_section: 'Profil',
    api_keys_section: 'API-Schlüssel',
    security_section: 'Sicherheit',
    loading: 'Kontoinformationen werden geladen...',
    profile: 'Profil',
    authorProfile: 'Autor-Profil',
    publisherProfile: 'Publisher-Profil',
    aggregatorProfile: 'Aggregator-Profil',
    id: 'ID',
    name: 'Name',
    email: 'E-Mail',
    services: 'Services',
    memberSince: 'Mitglied seit',
    lastLogin: 'Letzte Anmeldung',
    createNewApiKey: 'Neuen API-Schlüssel erstellen',
    apiKeysTitle: 'API-Schlüssel',
    apiKeys: {
      name: 'Name',
      keyId: 'Schlüssel-ID',
      status: 'Status',
      lastUsed: 'Zuletzt verwendet',
      created: 'Erstellt',
      expires: 'Läuft ab',
      neverUsed: 'Nie verwendet',
      noKeys: 'Keine API-Schlüssel gefunden.',
      advancedControls: 'Für erweiterte Steuerungen besuchen Sie',
      advancedControlsLink: 'die Kontoverwaltungsseite'
    },
    security: {
      changePassword: 'Passwort ändern',
      changePasswordDescription: 'Aktualisieren Sie Ihr Kontopasswort. Sie müssen Ihr aktuelles Passwort eingeben, um die Änderung zu bestätigen.',
      currentPassword: 'Aktuelles Passwort',
      currentPasswordPlaceholder: 'Geben Sie Ihr aktuelles Passwort ein',
      newPassword: 'Neues Passwort',
      newPasswordPlaceholder: 'Geben Sie Ihr neues Passwort ein',
      confirmPassword: 'Neues Passwort bestätigen',
      confirmPasswordPlaceholder: 'Geben Sie Ihr neues Passwort erneut ein',
      changePasswordButton: 'Passwort ändern',
      changingPassword: 'Wird geändert...',
      passwordChangedSuccess: 'Ihr Passwort wurde erfolgreich geändert.',
      passwordChangeFailed: 'Passwortänderung fehlgeschlagen. Bitte überprüfen Sie Ihr aktuelles Passwort und versuchen Sie es erneut.',
      resetPassword: 'Passwort zurücksetzen',
      resetPasswordDescription: 'Passwort vergessen? Wir können einen Zurücksetzungslink an Ihre registrierte E-Mail-Adresse senden. Klicken Sie auf den Link in der E-Mail, um ein neues Passwort festzulegen.',
      sendResetEmailButton: 'Passwort-Zurücksetzungs-E-Mail senden',
      sendingResetEmail: 'Wird gesendet...',
      resetEmailSent: 'Eine Passwort-Zurücksetzungs-E-Mail wurde an Ihre registrierte Adresse gesendet. Bitte überprüfen Sie Ihren Posteingang und folgen Sie dem Link, um ein neues Passwort festzulegen.',
      resetPasswordFailed: 'Passwort-Zurücksetzungs-E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      changePrimaryEmail: 'Primäre E-Mail ändern',
      changePrimaryEmailDescription: 'Aktualisieren Sie Ihre primäre E-Mail-Adresse. Eine Bestätigungs-E-Mail wird an die neue Adresse gesendet. Die neue E-Mail wird erst nach der Bestätigung aktiviert.',
      newEmail: 'Neue E-Mail',
      newEmailPlaceholder: 'Geben Sie Ihre neue E-Mail-Adresse ein',
      confirmEmail: 'Neue E-Mail bestätigen',
      confirmEmailPlaceholder: 'Geben Sie Ihre neue E-Mail-Adresse erneut ein',
      emailsDoNotMatch: 'E-Mail-Adressen stimmen nicht überein',
      changeEmailButton: 'Bestätigungs-E-Mail senden',
      sendingVerification: 'Wird gesendet...',
      emailVerificationSent: 'Eine Bestätigungs-E-Mail wurde an Ihre neue Adresse gesendet. Bitte überprüfen Sie Ihren Posteingang und folgen Sie den Anweisungen zur Aktivierung.',
      emailChangeFailed: 'E-Mail-Änderung fehlgeschlagen. Bitte versuchen Sie es erneut.'
    }
  },
  profileSetup: {
    displayName: 'Anzeigename',
    displayNamePlaceholder: 'Geben Sie Ihren Anzeigenamen ein',
    displayNameHelp: 'Ihr Anzeigename wird verwendet, um Sie im System zu identifizieren (dies ist nicht Ihr Benutzername).',
    title: 'Profil-Einrichtung',
    description: 'Bitte geben Sie Ihre beruflichen Informationen an, um Ihr Profil zu vervollständigen.',
    affiliation: 'Zugehörigkeit',
    affiliationPlaceholder: 'z.B., Universität Cambridge, MIT, Google Research',
    areaOfInterest: 'Interessensgebiet',
    areaOfInterestPlaceholder: 'z.B., Machine Learning, Computer Vision, NLP',
    homePage: 'Homepage',
    homePagePlaceholder: 'https://ihre-website.com',
    secondaryEmails: 'Sekundäre E-Mails',
    addSecondaryEmail: 'Sekundäre E-Mail hinzufügen',
    secondaryEmailPlaceholder: 'Ihre E-Mail-Adresse',
    secondaryEmailsHelp: 'Ihre sekundären E-Mails',
    save: 'Profil speichern',
    saving: 'Speichern...',
    nextPage: 'Nächste Seite',
    update: 'Profil aktualisieren',
    updating: 'Aktualisieren...',
    avatarUrl: 'Avatar-URL',
    avatarUrlPlaceholder: 'https://beispiel.com/avatar.jpg',
    changeAvatar: 'Avatar ändern',
    noAvatar: 'Kein Avatar',
    success: {
      saved: 'Profil erfolgreich gespeichert!'
    },
    errors: {
      affiliationRequired: 'Zugehörigkeit ist erforderlich.',
      areaOfInterestRequired: 'Interessensgebiet ist erforderlich.',
      invalidHomePage: 'Bitte geben Sie eine gültige URL für Ihre Homepage ein.',
      tooManySecondaryEmails: 'Sie können maximal 5 sekundäre E-Mails hinzufügen.',
      invalidSecondaryEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      saveFailed: 'Profil konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
      invalidAvatarUrl: 'Bitte geben Sie eine gültige Avatar-URL ein.'
    }
  },
  profile: {
    key_DISPLAY_NAME: 'Anzeigename',
    key_NAME: 'Name',
    key_EMAIL: 'E-Mail',
    key_AFFILIATION: 'Zugehörigkeit',
    key_AREA_OF_INTEREST: 'Interessensgebiet',
    key_HOME_PAGE: 'Homepage',
    key_EMAILS: 'E-Mails',
    key_AVATAR_URL: 'Avatar-URL',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: 'Profil-Einrichtung',
    description: 'Bitte richten Sie Ihr Profil ein, um auf diesen Service zuzugreifen.',
    update: 'Profil aktualisieren',
    editProfile: 'Profil bearbeiten'
  },
  bankInfoForm: {
    title: 'Bankinformationen hinzufügen/aktualisieren',
    accountHolder: 'Name des Kontoinhabers:',
    iban: 'IBAN:',
    bic: 'BIC/SWIFT-Code:',
    save: 'Bankinformationen speichern',
    saved: 'Bankinformationen lokal gespeichert (für diese Demo).'
  },
  settings: {
    title: 'Einstellungen',
    theme: 'Thema',
    light: 'Hell',
    dark: 'Dunkel',
    layout: 'Layout',
    helpEnabled: 'Hilfemeldungen anzeigen',
    sidebar: 'Seitenleiste',
    navbar: 'Navigationsleiste',
    save: 'Speichern',
    saved: 'Einstellungen gespeichert!',
    language: 'Sprache',
    experimentations: 'Experimentierungen',
    enabled: 'Aktiviert',
    disabled: 'Deaktiviert'
  },
  apiKeyModal: {
    title: 'Neuen API-Schlüssel erstellen',
    namePlaceholder: 'Name für den API-Schlüssel',
    nameHelp: 'Geben Sie einen Namen für den API-Schlüssel ein, dies hilft Ihnen später, den Schlüssel zu identifizieren.',
    createButton: 'API-Schlüssel erstellen',
    yourKeyIs: 'Ihr neuer API-Schlüssel ist:',
    copyWarning: 'Kopieren und speichern Sie diesen Schlüssel. Sie können ihn später nicht mehr einsehen!',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    close: 'Schließen',
    error: 'API-Schlüssel konnte nicht erstellt werden.'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: 'Zum hellen Modus wechseln',
    switchToDark: 'Zum dunklen Modus wechseln'
  }
};
